import { z } from "zod";

export const FILE_TYPES = ["pdf", "doc", "docx"] as const;
export type FileType = (typeof FILE_TYPES)[number];

export const RESUME_RENDER_FORMATS = ["json", "pdf", "both"] as const;

export const RESUME_TEMPLATES = [
  "classic",
  "modern",
  "ats",
  "soft-hard",
  "acorn",
  "finely",
  "imprecv",
  "simple",
  "ttq",
  "timeline",
  "executive",
  "lavandula",
  "nabcv",
] as const;
export type ResumeTemplate = (typeof RESUME_TEMPLATES)[number];

export const AGGRESSIVENESS_LEVELS = ["literal", "balanced", "aggressive"] as const;
export type Aggressiveness = (typeof AGGRESSIVENESS_LEVELS)[number];

/**
 * A length-capped array for LLM OUTPUT ONLY. Use it for every array below.
 *
 * The caps are page-length budgets: a resume that runs to three pages is a
 * broken product. But a plain `.max(n)` on a streamed LLM response is a crash,
 * not a budget — the AI SDK validates the whole object at finish (`streamObject`
 * on the server, `useObject` in the browser), so ONE array with n+1 items throws
 * away the entire rewrite, and the user is charged for nothing. That is how a
 * `summary` cap of 3 against a prompt asking for "max 4 short lines" produced
 * "Type validation failed" instead of a resume.
 *
 * `overwrite` runs before the `max` check, so the extra items are dropped and
 * the check then passes. `maxItems` still reaches the provider in the JSON
 * schema, so the model is still told the budget — it just cannot break us by
 * missing it. The ingest path gets the same effect from `sanitizeExtractedJson`,
 * which cannot be used here because the SDK parses inside the stream.
 *
 * Never use this on `improveResumeInput` — user input must be rejected loudly,
 * not silently truncated.
 */
export const capped = <T extends z.ZodTypeAny>(item: T, max: number) =>
  z
    .array(item)
    .overwrite((items) => items.slice(0, max))
    .max(max);

/**
 * The keyword list the rewrite hunts for.
 *
 * Optional *input*: today the model emits it as its own first output field
 * (see `improveResumeLlmOutput`). When the job pipeline lands it gets extracted
 * once per job at ingest and passed in here instead, and nothing else changes.
 */
export const targetProfileSchema = z.object({
  keywords: capped(
    z.object({
      canonical: z.string(),
      tier: z.enum(["core", "secondary", "tail"]),
      // "jd" = literally in the job description. "role_norm" = the JD omitted it
      // but the role always implies it (a React job never lists HTML).
      from: z.enum(["jd", "role_norm"]),
    }),
    40,
  ),
  // agile, cross-functional, explain-to-non-technical …
  soft_requirements: capped(z.string(), 10),
});

export type TargetProfile = z.infer<typeof targetProfileSchema>;

export const improveResumeInput = z
  .object({
    text: z.string().min(1).max(20_000).optional(),
    file: z.string().optional(),
    file_type: z.enum(FILE_TYPES).optional(),
    job_title: z.string().min(1).max(180),
    job_description: z.string().min(1).max(20_000),
    additional_info: z.string().max(2_000).optional(),
    template: z.enum(RESUME_TEMPLATES).optional().default("simple"),
    aggressiveness: z.enum(AGGRESSIVENESS_LEVELS).optional().default("balanced"),
    target_profile: targetProfileSchema.optional(),
  })
  .refine((d) => d.text || (d.file && d.file_type), {
    message: 'Provide "text", or both "file" and "file_type"',
  });

export type ImproveResumeInput = z.infer<typeof improveResumeInput>;

const optimizedLinkSchema = z.object({
  label: z.string(),
  url: z.string(),
});

const optimizedExperienceSchema = z.object({
  title: z.string(),
  company: z.string(),
  location: z.string().nullable(),
  date_start: z.string().nullable(),
  date_end: z.string().nullable(),
  bullets: capped(z.string(), 12),
});

const optimizedEducationSchema = z.object({
  institution: z.string(),
  degree: z.string().nullable(),
  field: z.string().nullable(),
  location: z.string().nullable(),
  date: z.string().nullable(),
  details: capped(z.string(), 10),
});

const optimizedProjectSchema = z.object({
  name: z.string(),
  // Legacy single link. Still honored, but prefer `links` for multiple links.
  url: z.string().nullable(),
  // Arbitrary labelled links (e.g. GitHub, Live). Rendered right-aligned by
  // templates and wrapped to the next line when they do not fit on one line.
  // Note: Groq & OpenAI strict JSON schemas require every property to be listed in `required`.
  // Do NOT use `.default([])` or `.optional()` here because Zod omits defaulted/optional keys from `required`.
  links: capped(optimizedLinkSchema, 8),
  description: z.string().nullable(),
  bullets: capped(z.string(), 10),
});

const optimizedCertificationSchema = z.object({
  name: z.string(),
  issuer: z.string().nullable(),
  date: z.string().nullable(),
});

const optimizedCustomSectionSchema = z.object({
  heading: z.string(),
  paragraphs: capped(z.string(), 6),
  bullets: capped(z.string(), 15),
});

export const optimizedResumeDocument = z.object({
  personal: z.object({
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    location: z.string().nullable(),
    links: capped(optimizedLinkSchema, 6),
  }),
  company: z.string().nullable(),
  headline: z.string().nullable(),
  // 4, matching the SUMMARY block's own "hard cap: 4 short lines". This was 3,
  // and a correct 4-line summary killed the whole rewrite.
  summary: capped(z.string(), 4),
  skills: capped(
    z.object({
      category: z.string(),
      // No `min(1)`: an empty category renders as nothing, but as a check it
      // would fail the entire rewrite over one stray heading.
      items: capped(z.string(), 18),
    }),
    8,
  ),
  // These caps are page-length budgets, and they truncate from the end — the
  // prompt already orders roles, projects and bullets most-relevant-first, so
  // the items dropped are the ones a human would cut to reach one page.
  experience: capped(optimizedExperienceSchema, 8),
  projects: capped(optimizedProjectSchema, 6),
  education: capped(optimizedEducationSchema, 5),
  certifications: capped(optimizedCertificationSchema, 8),
  custom_sections: capped(optimizedCustomSectionSchema, 5),
});

export type OptimizedResumeDocument = z.infer<typeof optimizedResumeDocument>;

/**
 * Points at one bullet. A structured triple, not a string.
 *
 * A model asked for a string ref answers "experience[0].bullets[2]" one call and
 * "Ganethra IT Services, bullet 3" the next. Every validator would then need a
 * parser for free-form English. Three fields cost the model nothing and the
 * validators resolve them with an array index.
 */
const bulletRefSchema = z.object({
  section: z.enum(["experience", "projects"]),
  item: z.number().int().min(0),
  bullet: z.number().int().min(0),
});

export type BulletRef = z.infer<typeof bulletRefSchema>;

const keywordAssignmentSchema = z.object({
  keyword: z.string(),
  tier: z.enum(["core", "secondary", "tail"]),
  bullet_ref: bulletRefSchema,
  // The source fact the claim rests on.
  basis: z.string(),
  derivation: z.enum(["stated", "implied", "promoted", "correlated", "practice"]),
  risk: z.enum(["none", "low", "medium"]),
});

export type KeywordAssignment = z.infer<typeof keywordAssignmentSchema>;

/**
 * Field order is the design, not cosmetics — see plan §4.
 *
 * Structured generation emits fields in schema order, so putting the hunt list
 * and the placement map before `optimized_resume` makes the model commit to
 * both *before* it writes a single bullet. That is chain-of-thought inside one
 * call. Reordering these keys silently removes the constraint and the placement
 * rules (R3, R5) stop binding.
 *
 * Note: Groq and OpenAI strict JSON mode require every property in `properties` to
 * be explicitly listed in the `required` array. Using Zod `.default(...)` or `.optional()`
 * omits keys from `required` during JSON Schema generation, causing Groq `AI_APICallError`.
 */
export const improveResumeLlmOutput = z.object({
  // 1. commit to the hunt list first
  target_profile: targetProfileSchema,

  // 2. then decide placement + basis, before writing anything
  keyword_assignments: capped(keywordAssignmentSchema, 60),

  // 3. then write, constrained by 1 and 2
  optimized_resume: optimizedResumeDocument,

  // 4. then report honestly against its own list
  coverage: z.object({
    found: capped(
      z.object({
        keyword: z.string(),
        tier: z.enum(["core", "secondary", "tail"]),
        placed_in: z.string(),
      }),
      40,
    ),
    missing: capped(
      z.object({
        keyword: z.string(),
        tier: z.enum(["core", "secondary", "tail"]),
        why_not: z.string(),
      }),
      40,
    ),
  }),
  gap_questions: capped(
    z.object({
      keyword: z.string(),
      question: z.string(),
    }),
    12,
  ),

  // 5. existing
  changes: capped(
    z.object({
      section: z.string(),
      change: z.string(),
      reason: z.string(),
      source_evidence: z.string().nullable(),
    }),
    24,
  ),
  warnings: capped(z.string(), 12),

  // Hard eligibility requirements this candidate cannot satisfy by supplying more
  // information — wrong country for an on-site role, sponsorship the employer will
  // not offer, a clearance they do not hold. Anything the candidate COULD clear by
  // stating a fact is a gap_question instead; that split is what keeps this list
  // short and is enforced in the prompt, not here.
  //
  // .default([]) is load-bearing. The prompt tells the model an empty array is the
  // normal case, so the model with nothing to report is the one most likely to omit
  // the key entirely — and streamObject validates this schema at finish. Required,
  // that omission would fail an otherwise-perfect resume over a field that had
  // nothing to say. The default also keeps the output type string[], not optional.
  blockers: capped(z.string(), 4).default([]),
});

export type ImproveResumeLlmOutput = z.infer<typeof improveResumeLlmOutput>;
